<template>
  <div
    class="rounded bg-white p-2 shadow-normal shadow-gray-400"
    :class="isLogin ? 'w-[160px]' : 'w-[550px]'"
  >
    <div class="flex">
      <ul v-if="!isLogin" class="w-3/6 pr-1">
        <li v-for="item in groupingRegionsDisplay" :key="hash(item)">
          <button
            v-if="!item.regionGroupCode"
            class="locale-button"
            :class="
              selectedRegion === (item as IRegionDisplay).regionCode &&
              'current'
            "
            :title="item.displayName"
            @click="handleClickRegion((item as IRegionDisplay).regionCode)"
          >
            <span class="block w-full break-words">
              {{ item.displayName }}
            </span>
            <IconCaret direction="right" class="locale-button-arrow" />
          </button>
          <div v-else>
            <p class="w-full rounded px-2 py-1 text-left text-base font-bold">
              {{ item.displayName }}
            </p>
            <ul class="pl-4">
              <li
                v-for="subItem in (item as IRegionMenuDisplay).regions"
                :key="subItem.regionCode"
              >
                <button
                  class="locale-button"
                  :class="selectedRegion === subItem.regionCode && 'current'"
                  :title="subItem.displayName"
                  @click="handleClickRegion(subItem.regionCode)"
                >
                  <span class="block w-full break-words">
                    {{ subItem.displayName }}
                  </span>
                  <IconCaret direction="right" class="locale-button-arrow" />
                </button>
              </li>
            </ul>
          </div>
        </li>
      </ul>
      <ul :class="!isLogin ? 'w-3/6 pl-1' : 'w-full'">
        <li v-for="item in contextWebsitesDisplay" :key="item.code">
          <a
            class="locale-button"
            :href="provideLocalePath(item.code)"
            :class="locale === item.code && 'current'"
            :title="item.displayName"
          >
            <span class="block w-full break-words">
              {{ item.displayName }}
            </span>
            <IconCheck
              v-if="locale === item.code"
              direction="right"
              class="locale-button-arrow"
            />
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
  import hash from 'object-hash';

  import type { IRegion, IWebsite } from '@/assets/types/api/Common';
  import type { IRegionMenu } from '@/composables/useLanguage';

  type IRegionDisplay = IRegion & { displayName: string };
  type IRegionMenuDisplay = Omit<IRegionMenu, 'regions'> & {
    displayName: string;
    regions: IRegionDisplay[];
  };

  const { locale } = useI18n();
  const mainStore = useMainStore();
  const profileStore = useProfileStore();
  const { regions, regionCode } = storeToRefs(mainStore);
  const { isLogin } = storeToRefs(profileStore);
  const {
    provideLocalePath,
    provideLocaleName,
    provideRegionName,
    provideRegionGroupName,
    contextRegions,
    groupingRegions,
  } = useLanguage();

  const selectedRegion = ref<string>('');

  const contextWebsites = computed((): IWebsite[] => {
    const condition = isLogin.value ? regionCode.value : selectedRegion.value;
    const regionItem = contextRegions.value.find(
      ({ regionCode }) => regionCode === condition
    );
    if (regionItem) {
      return regionItem.websites;
    }
    return [];
  });

  const groupingRegionsDisplay = computed(
    (): (IRegionDisplay | IRegionMenuDisplay)[] =>
      groupingRegions.value.map((item) => {
        if (!item.regionGroupCode) {
          const region = item as IRegion;
          return {
            ...region,
            displayName: provideRegionName(region.regionCode),
          };
        }
        const menu = item as IRegionMenu;
        return {
          ...menu,
          displayName: provideRegionGroupName(menu.regionGroupCode),
          regions: menu.regions.map((subItem) => ({
            ...subItem,
            displayName: provideRegionName(subItem.regionCode),
          })),
        };
      })
  );

  const contextWebsitesDisplay = computed(
    (): (IWebsite & { displayName: string })[] =>
      contextWebsites.value.map((item) => ({
        ...item,
        displayName: provideLocaleName(item.code),
      }))
  );

  const handleClickRegion = (code: EnumRegion): void => {
    selectedRegion.value = code;
  };

  onMounted(() => {
    const regionItem = regions.value.find(({ websites }) =>
      websites.find((item) => item.code === locale.value)
    );

    if (regionItem) {
      selectedRegion.value = regionItem.regionCode;
    } else if (regions.value.length) {
      selectedRegion.value = regions.value[0].regionCode;
    } else {
      selectedRegion.value = '';
    }
  });
</script>

<style lang="scss" scoped>
  .locale-button {
    @apply relative block w-full rounded py-1 pl-2 pr-7 text-left text-base font-bold hover:text-blue-400 hover:underline;

    .locale-button-arrow {
      @apply absolute right-1 top-1 block h-6 w-6 text-gray-600;
    }

    &.current {
      @apply bg-blue-100 text-blue-400;

      .locale-button-arrow {
        @apply text-blue-400;
      }
    }
  }
</style>
